import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Eye } from "lucide-react";
import api, { trackEvent } from "../lib/api";
import BathroomPreview from "../components/BathroomPreview";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";

const calcJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Bathroom Remodel Cost Calculator",
  url: `${SITE_URL}/calculator`,
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Any",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  description:
    "Step-by-step bathroom remodel cost calculator. Enter your bathroom size, finish level, fixtures, and ZIP — get an instant cost range plus a contractor-ready brief.",
};

const STEPS = [
  "size",
  "remodel_type",
  "finish_level",
  "material",
  "fixtures",
  "zip_code",
];

const STEP_LABELS = {
  size: "Bathroom size",
  remodel_type: "Remodel scope",
  finish_level: "Finish level",
  material: "Primary tile material",
  fixtures: "Fixture upgrades",
  zip_code: "Location",
};

const STEP_HELPER = {
  size:
    "Drag the slider — your floor plan resizes live. Most U.S. baths fall between 30 and 100 sq ft.",
  remodel_type:
    "The single biggest cost driver. The badge in the preview updates with your pick.",
  finish_level:
    "Drives fixture quality, cabinet construction, and tile grade. The room's color palette shifts as you choose.",
  material:
    "Floor and wall tile is one of the largest material costs. Watch the floor pattern change in the preview.",
  fixtures:
    "Pick everything you'd like included. Each fixture appears in the preview as you select it.",
  zip_code:
    "Labor rates vary by 30%+ across U.S. regions. We use the ZIP to localize your estimate.",
};

export default function Calculator() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    size_sqft: 40,
    remodel_type: "partial",
    finish_level: "mid",
    material: "ceramic",
    fixtures: [],
    zip_code: "",
  });

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    trackEvent("calculator_started");
    // api, setConfig, trackEvent are stable module/hook references. Empty deps
    // means "run once on mount" which is exactly what we want here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepKey = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  const canAdvance = useMemo(() => {
    if (stepKey === "zip_code") return form.zip_code.length === 5;
    if (stepKey === "size") return form.size_sqft >= 15 && form.size_sqft <= 400;
    return true;
  }, [stepKey, form]);

  const next = () => {
    if (!canAdvance) return;
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      trackEvent("calculator_step", { step: STEPS[step + 1] });
    } else {
      submit();
    }
  };
  const prev = () => step > 0 && setStep(step - 1);

  const toggleFixture = (id) => {
    setForm((f) => ({
      ...f,
      fixtures: f.fixtures.includes(id)
        ? f.fixtures.filter((x) => x !== id)
        : [...f.fixtures, id],
    }));
  };

  const submit = async () => {
    setSubmitting(true);
    trackEvent("calculator_submitted", { ...form });
    try {
      const res = await api.post("/estimate", form);
      navigate(`/results/${res.data.id}`, {
        state: { teaser: res.data, form },
      });
    } catch (e) {
      console.error(e);
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="container-x py-32 text-center text-ink-muted">
        Loading calculator…
      </div>
    );
  }

  return (
    <div data-testid="calculator-page" className="bg-grain">
      <SEO
        title="Free Bathroom Remodel Cost Calculator"
        description="Calculate your bathroom remodel cost in under 2 minutes. Get a low/expected/high range, timeline, hidden-cost warnings, and a printable contractor brief."
        path="/calculator"
        jsonLd={calcJsonLd}
      />
      <div className="container-x py-10 lg:py-16 max-w-6xl">
        {/* Progress */}
        <div className="mb-10" data-testid="calculator-progress">
          <div className="flex items-center justify-between mb-3">
            <p className="overline">
              Step {step + 1} of {STEPS.length}
            </p>
            <p className="font-mono text-xs text-ink-muted">
              {STEP_LABELS[stepKey]}
            </p>
          </div>
          <div className="h-[3px] bg-line rounded-full overflow-hidden">
            <div
              className="h-full bg-brand transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* ─────── FORM column ─────── */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {/* SIZE */}
                {stepKey === "size" && (
                  <Step title="How big is the bathroom?" subtitle={STEP_HELPER.size}>
                    <div className="surface-card p-7">
                      <div className="flex items-baseline gap-2">
                        <input
                          data-testid="input-size"
                          type="number"
                          min="15"
                          max="400"
                          value={form.size_sqft}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              size_sqft: Number(e.target.value) || 0,
                            })
                          }
                          className="text-5xl font-display bg-transparent border-b-2 border-line focus:border-brand focus:outline-none w-28 text-ink"
                        />
                        <span className="text-ink-muted text-base">sq ft</span>
                      </div>
                      <input
                        data-testid="slider-size"
                        type="range"
                        min="15"
                        max="200"
                        value={form.size_sqft}
                        onChange={(e) =>
                          setForm({ ...form, size_sqft: Number(e.target.value) })
                        }
                        className="w-full mt-7 accent-brand"
                      />
                      <div className="flex justify-between text-xs font-mono text-ink-faint mt-2">
                        <span>Half bath</span>
                        <span>5×8</span>
                        <span>Master</span>
                      </div>
                    </div>
                  </Step>
                )}

                {/* REMODEL TYPE */}
                {stepKey === "remodel_type" && (
                  <Step
                    title="What kind of remodel?"
                    subtitle={STEP_HELPER.remodel_type}
                  >
                    <OptionGrid
                      testid="options-remodel-type"
                      options={config.remodel_types}
                      selected={[form.remodel_type]}
                      onSelect={(id) => setForm({ ...form, remodel_type: id })}
                    />
                  </Step>
                )}

                {/* FINISH */}
                {stepKey === "finish_level" && (
                  <Step
                    title="What finish level?"
                    subtitle={STEP_HELPER.finish_level}
                  >
                    <OptionGrid
                      testid="options-finish-level"
                      options={config.finish_levels}
                      selected={[form.finish_level]}
                      onSelect={(id) => setForm({ ...form, finish_level: id })}
                    />
                  </Step>
                )}

                {/* MATERIAL */}
                {stepKey === "material" && (
                  <Step
                    title="Primary tile material?"
                    subtitle={STEP_HELPER.material}
                  >
                    <OptionGrid
                      testid="options-material"
                      options={config.materials}
                      selected={[form.material]}
                      onSelect={(id) => setForm({ ...form, material: id })}
                    />
                  </Step>
                )}

                {/* FIXTURES */}
                {stepKey === "fixtures" && (
                  <Step
                    title="Any fixture upgrades?"
                    subtitle={STEP_HELPER.fixtures}
                  >
                    <div
                      data-testid="options-fixtures"
                      className="grid sm:grid-cols-2 gap-3"
                    >
                      {config.fixtures.map((opt) => {
                        const active = form.fixtures.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            data-testid={`fixture-${opt.id}`}
                            onClick={() => toggleFixture(opt.id)}
                            className={`text-left p-5 rounded-lg border transition-all ${
                              active
                                ? "border-brand bg-brand-tint shadow-sm"
                                : "border-line bg-surface hover:border-ink-faint"
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <p className="font-medium">{opt.label}</p>
                              <span
                                className={`w-5 h-5 rounded grid place-items-center border ${
                                  active
                                    ? "bg-brand border-brand text-white"
                                    : "border-line"
                                }`}
                              >
                                {active && <Check size={13} strokeWidth={3} />}
                              </span>
                            </div>
                            <p className="text-xs text-ink-muted mt-1.5">
                              {opt.desc}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </Step>
                )}

                {/* ZIP */}
                {stepKey === "zip_code" && (
                  <Step title="What's your ZIP code?" subtitle={STEP_HELPER.zip_code}>
                    <div className="surface-card p-7">
                      <input
                        data-testid="input-zip"
                        inputMode="numeric"
                        pattern="[0-9]{5}"
                        maxLength={5}
                        placeholder="90210"
                        value={form.zip_code}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            zip_code: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 5),
                          })
                        }
                        className="text-5xl font-display bg-transparent border-b-2 border-line focus:border-brand focus:outline-none w-48 text-ink tracking-wider"
                      />
                      <p className="text-xs text-ink-muted mt-4 font-mono">
                        5-digit U.S. ZIP code
                      </p>
                    </div>
                  </Step>
                )}
              </motion.div>
            </AnimatePresence>

            {/* NAV */}
            <div className="mt-10 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={prev}
                disabled={step === 0}
                data-testid="btn-back"
                className="btn-ghost"
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                type="button"
                onClick={next}
                disabled={!canAdvance || submitting}
                data-testid="btn-next"
                className="btn-primary px-7 py-3.5"
              >
                {submitting
                  ? "Calculating…"
                  : step === STEPS.length - 1
                    ? "See my estimate"
                    : "Continue"}
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* ─────── PREVIEW column ─────── */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="surface-card p-5 lg:p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="overline flex items-center gap-2">
                    <Eye size={12} /> Live preview
                  </p>
                  <span className="font-mono text-[11px] text-ink-faint">
                    Top-down view
                  </span>
                </div>
                <BathroomPreview
                  size_sqft={form.size_sqft}
                  remodel_type={form.remodel_type}
                  finish_level={form.finish_level}
                  material={form.material}
                  fixtures={form.fixtures}
                />
              </div>
              <p className="mt-4 text-xs text-ink-muted leading-relaxed px-1">
                The illustration above is a visualization — actual layout will
                depend on your existing plumbing locations.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const Step = ({ title, subtitle, children }) => (
  <div>
    <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-3">
      {title}
    </h1>
    <p className="text-ink-muted max-w-xl mb-7 leading-relaxed">{subtitle}</p>
    {children}
  </div>
);

const OptionGrid = ({ options, selected, onSelect, testid }) => (
  <div data-testid={testid} className="grid sm:grid-cols-2 gap-3">
    {options.map((opt) => {
      const active = selected.includes(opt.id);
      return (
        <button
          key={opt.id}
          type="button"
          data-testid={`option-${opt.id}`}
          onClick={() => onSelect(opt.id)}
          className={`text-left p-5 rounded-lg border transition-all min-h-[110px] ${
            active
              ? "border-brand bg-brand-tint shadow-sm"
              : "border-line bg-surface hover:border-ink-faint hover:-translate-y-[1px]"
          }`}
        >
          <div className="flex items-start justify-between">
            <p className="font-display text-lg">{opt.label}</p>
            {active && (
              <span className="w-6 h-6 rounded-full bg-brand text-white grid place-items-center">
                <Check size={14} strokeWidth={3} />
              </span>
            )}
          </div>
          <p className="text-sm text-ink-muted mt-2 leading-relaxed">
            {opt.desc}
          </p>
        </button>
      );
    })}
  </div>
);
